/**
 * LiveTeamActivity — Admin-only Dashboard panel showing every agent/admin's
 * attendance state + total active hours for a chosen IST date.
 *
 * Today (default): auto-refreshes every 30s; rows tick the timer locally for
 * currently-active users.
 *
 * Past date (via "Last 3 days" / "Last 7 days" shortcut or Custom picker):
 * static snapshot for that specific IST date — status is either Active (they
 * attended) or Absent (no record). No live tick.
 */
import { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Activity, CheckCircle2, PauseCircle, AlertCircle, Clock } from "lucide-react";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { API } from "../App";
import { formatActiveDuration } from "./AttendanceGate";

const statusMeta = {
  active: { label: "Active", cls: "bg-emerald-50 text-emerald-700 border-emerald-200", Icon: CheckCircle2 },
  inactive: { label: "Inactive", cls: "bg-orange-50 text-orange-700 border-orange-200", Icon: PauseCircle },
  pending: { label: "Pending", cls: "bg-amber-50 text-amber-700 border-amber-200", Icon: Clock },
  absent: { label: "Absent", cls: "bg-rose-50 text-rose-700 border-rose-200", Icon: AlertCircle },
};

// Returns today's date in IST as YYYY-MM-DD (server-side truth is IST).
const todayIST = () => {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());
  const map = Object.fromEntries(parts.map((p) => [p.type, p.value]));
  return `${map.year}-${map.month}-${map.day}`;
};

const addDays = (isoDate, offset) => {
  // Interpret the IST date at UTC midnight so arithmetic doesn't slide.
  const d = new Date(`${isoDate}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + offset);
  return d.toISOString().slice(0, 10);
};

const LiveTeamActivity = () => {
  const today = useMemo(() => todayIST(), []);
  const [selectedDate, setSelectedDate] = useState(today); // YYYY-MM-DD (IST)
  const [pickerOpen, setPickerOpen] = useState(false);
  const [data, setData] = useState(null);
  const [fetchedAt, setFetchedAt] = useState(Date.now());
  const [tick, setTick] = useState(0);

  const isToday = selectedDate === today;

  const load = useCallback(async () => {
    try {
      const url = isToday
        ? `${API}/attendance/today`
        : `${API}/attendance/today?date=${selectedDate}`;
      const res = await axios.get(url);
      setData(res.data);
      setFetchedAt(Date.now());
    } catch (_) { /* ignore (non-admins get 403) */ }
  }, [selectedDate, isToday]);

  useEffect(() => {
    load();
    if (!isToday) return;
    const refresh = setInterval(load, 30000);
    const ticker = setInterval(() => setTick((t) => t + 1), 1000);
    return () => { clearInterval(refresh); clearInterval(ticker); };
  }, [load, isToday]);

  const liveSecondsFor = useCallback((u) => {
    let s = u.total_active_seconds || 0;
    if (isToday && u.current_state === "active") {
      s += Math.max(0, Math.floor((Date.now() - fetchedAt) / 1000));
    }
    return s;
  }, [isToday, fetchedAt]);

  if (!data || !data.users) return null;
  void tick;

  const prettyDate = new Date(`${selectedDate}T00:00:00Z`).toLocaleDateString("en-GB", {
    day: "2-digit", month: "short", year: "numeric", timeZone: "UTC",
  });

  const filterButtons = [
    { key: "today", label: "Today", date: today },
    { key: "d3", label: "3 days ago", date: addDays(today, -3) },
    { key: "d7", label: "7 days ago", date: addDays(today, -7) },
  ];

  return (
    <Card className="border-slate-200" data-testid="live-team-activity">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-3 flex-wrap">
            <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2">
              <Activity className="w-5 h-5 text-emerald-600" />
              {isToday ? "Live Team Activity" : `Team Activity — ${prettyDate}`}
            </CardTitle>
            <div className="flex items-center gap-1 flex-wrap" data-testid="live-team-date-filter">
              {filterButtons.map((b) => (
                <Button
                  key={b.key}
                  size="sm"
                  variant={selectedDate === b.date && !pickerOpen ? "default" : "outline"}
                  className="h-7 text-xs"
                  onClick={() => { setPickerOpen(false); setSelectedDate(b.date); }}
                  data-testid={`live-team-filter-${b.key}`}
                >
                  {b.label}
                </Button>
              ))}
              <Button
                size="sm"
                variant={pickerOpen ? "default" : "outline"}
                className="h-7 text-xs"
                onClick={() => setPickerOpen((v) => !v)}
                data-testid="live-team-filter-custom"
              >
                Custom date
              </Button>
              {pickerOpen && (
                <Input
                  type="date"
                  value={selectedDate}
                  max={today}
                  onChange={(e) => e.target.value && setSelectedDate(e.target.value)}
                  className="h-7 text-xs w-40"
                  data-testid="live-team-filter-date-input"
                />
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 text-xs">
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
              {isToday && (
                <span className="relative flex h-1.5 w-1.5">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-500"></span>
                </span>
              )}
              {data.active_count} {isToday ? "Active" : "Present"}
            </span>
            {isToday && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-orange-50 text-orange-700 border border-orange-200">
                {data.inactive_count} Inactive
              </span>
            )}
            {isToday && data.pending_count > 0 && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
                {data.pending_count} Pending
              </span>
            )}
            {data.absent_count > 0 && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-rose-50 text-rose-700 border border-rose-200">
                {data.absent_count} Absent
              </span>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {data.users.length === 0 ? (
          <p className="text-center text-slate-400 text-sm py-6">No agents found</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="live-team-activity-table">
              <thead className="bg-slate-50 border-y border-slate-200">
                <tr>
                  <th className="text-left text-xs font-semibold text-slate-600 uppercase tracking-wide py-2.5 px-4">Agent</th>
                  <th className="text-left text-xs font-semibold text-slate-600 uppercase tracking-wide py-2.5 px-4">Role</th>
                  <th className="text-center text-xs font-semibold text-slate-600 uppercase tracking-wide py-2.5 px-4">Status</th>
                  <th className="text-center text-xs font-semibold text-slate-600 uppercase tracking-wide py-2.5 px-4">Marked At</th>
                  <th className="text-right text-xs font-semibold text-slate-600 uppercase tracking-wide py-2.5 px-4">
                    {isToday ? "Active Time Today" : "Active Time"}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {data.users.map((u) => {
                  const meta = statusMeta[u.status] || statusMeta.pending;
                  const Icon = meta.Icon;
                  const sec = liveSecondsFor(u);
                  return (
                    <tr key={u.user_id} className="hover:bg-slate-50" data-testid={`live-row-${u.user_id}`}>
                      <td className="py-2.5 px-4">
                        <div className="font-medium text-slate-900">{u.name}</div>
                        <div className="text-xs text-slate-500">{u.email}</div>
                      </td>
                      <td className="py-2.5 px-4 text-slate-600 capitalize">{u.role === "user" ? "Agent" : u.role}</td>
                      <td className="py-2.5 px-4 text-center">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${meta.cls}`}>
                          <Icon className="w-3 h-3" />
                          {isToday
                            ? meta.label
                            : (u.status === "active" ? "Present" : meta.label)}
                          {u.status === "active" && u.marked_late ? " (late)" : ""}
                        </span>
                      </td>
                      <td className="py-2.5 px-4 text-center text-slate-600 text-xs tabular-nums">
                        {u.marked_active_at ? new Date(u.marked_active_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—"}
                      </td>
                      <td className="py-2.5 px-4 text-right tabular-nums font-medium text-slate-900">
                        {sec > 0 ? formatActiveDuration(sec) : "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default LiveTeamActivity;
